import { type EventContentArg } from "@fullcalendar/core"
import { Flex, Typography } from "antd"
import dayjs from "dayjs"
import React from "react"
import { useTranslation } from "react-i18next"

import { CalendarTimeAndType } from "../CalendarTimeAndType.tsx"

import {
  estimateEventHeightPx,
  pickEventCardTier,
  timeRangeStart
} from "../../helpers/calendar-event-card-layout.ts"
import {
  calendarEventCardAddress,
  calendarEventCardContextName,
  calendarEventCardRecordParts,
  calendarEventCardText
} from "../../helpers/calendar-event-card-text.ts"
import { useElementSize } from "../../hooks/use-element-size.ts"

const { Text } = Typography

interface CalendarEventCellEventProps {
  eventInfo: EventContentArg
}

const TypeDot = ({ color }: { color?: string }) => (
  <span
    className="inline-block shrink-0 size-1.5 rounded-full"
    style={{ backgroundColor: color }}
  />
)

export function CalendarEventCellEvent({
  eventInfo
}: CalendarEventCellEventProps) {
  const { t } = useTranslation()
  const event = eventInfo?.event
  const input = { title: event?.title, record: event?.extendedProps }
  const { title: storedTitle, typeLabel } = calendarEventCardText(input, t)
  // The grid card's one scannable headline is the appointment's best
  // available IDENTITY, not a bare type — a bare "Proposal" gives almost no
  // context to distinguish it from every other Proposal on the grid.
  // `calendarEventCardText` resolves that identity through one shared
  // fallback chain (customer, then a real typed title, then the linked
  // lead, then the linked project — see `calendarEventCardContextName`) and
  // already bakes it into `storedTitle` with the type prefixed
  // ("Proposal: Review revised estimate"), returning `typeLabel: null` in
  // that case so it never prints twice; `typeLabel` only survives here when
  // NONE of those resolved, which is when the bare type is genuinely the
  // best we have. A legacy event with no category at all still falls back
  // to its stored title.
  const title = typeLabel || storedTitle
  const { recordName, personName } = calendarEventCardRecordParts(input, t)
  // Whatever the headline resolved to (customer, title, lead, or project),
  // repeating that exact value on a context line would just echo the
  // headline back — each line falls through to whatever's left once its
  // own value is already spoken for.
  const headlineContextName = calendarEventCardContextName(input, t)
  const isRecordInTitle = !!headlineContextName && headlineContextName === recordName
  const isPersonInTitle = !!headlineContextName && headlineContextName === personName
  const effectiveRecordName = isRecordInTitle ? null : recordName
  const personOrAddress =
    (isPersonInTitle ? null : personName) || calendarEventCardAddress(input)
  // The record (lead/project) takes the first context line when there is
  // one; an event with no lead/project (e.g. a personal event with just a
  // contact) puts the person/address there instead rather than waiting for
  // the taller second-line threshold that only makes sense once a first
  // line already exists to add a second one after.
  const firstContextText = effectiveRecordName || personOrAddress
  const secondContextText = effectiveRecordName ? personOrAddress : null

  // The card surfaces the event's relevant fields — who/what the meeting is
  // for, then time — compressing the LAYOUT as the box gets smaller rather
  // than deleting the data: a taller box stacks title/time/record/person on
  // their own lines; a shorter one drops time and the context lines in that
  // order, since there's no room to add them without reading as a details
  // view. Identity (the title) is never one of the things dropped, and
  // never shares its row with anything else — see the priority comment
  // below the month-view branch.
  //
  // What decides all of this is the box's real rendered size, not its
  // duration: FullCalendar's `eventMinHeight` (Calendar.tsx, a true safety
  // floor — see its own comment) and calendar.less' `@slot-height` are what
  // make a real 15-minute event genuinely shorter than a real 30-minute
  // one, so a height/width cutoff can decide this safely where a duration
  // cutoff (`diffInMinutes >= 30`) once put lines on a box that had no
  // pixels for them (confirmed live; invisible to jsdom).
  const diffInMinutes = dayjs(event?.end).diff(dayjs(event?.start), "minute")
  const { ref: sizeRef, size } = useElementSize<HTMLDivElement>({
    width: 200,
    height: estimateEventHeightPx(diffInMinutes)
  })
  // Duration/box-size drives which lines show — never how big any of them
  // are. Typography, gap and padding are fixed design-system sizes for
  // every event regardless of duration: a 60-minute card is not a 30-minute
  // card "scaled up", it's the same card with more empty space below it
  // (reported live — a duration-scaled title/time/gap made a 60+ minute
  // card read as an oversized data block next to its neighbors).
  const { timePlacement, timeContent, showContext, showSecondContext } =
    pickEventCardTier(size)
  const TITLE_SIZE_CLASS = "text-[13px]"
  const TIME_SIZE_CLASS = "text-[11px]"
  const DETAIL_SIZE_CLASS = "text-[11px]"
  // `leading-[1.2]` (not `leading-none`) gives every line just enough
  // headroom for descenders that a real browser's glyph metrics need past
  // exactly 1x font-size — confirmed live; jsdom can't see a clipped
  // descender. The small fixed gap keeps adjacent lines from visually
  // touching without needing to grow with the box.
  const LINE_GAP = 2
  // Indents the stacked time/context lines under the title text rather than
  // the dot — matches the reviewed mock, and keeps every secondary line's
  // left edge aligned with the one above it.
  const secondaryLineIndent = "pl-[10px]"
  const timeString =
    timePlacement !== "stacked" || !eventInfo?.timeText
      ? null
      : timeContent === "full"
        ? eventInfo.timeText
        : timeRangeStart(eventInfo.timeText)

  // Struck through rather than hidden: whoever was invited needs to see that
  // the meeting was cancelled, not find it silently gone from the grid.
  const isCancelled = event?.extendedProps?.status === "CANCELLED"

  // Month cells are a fraction of a week/day slot's height, so they get one
  // line.
  const isMonthView = eventInfo?.view?.type === "dayGridMonth"
  // FullCalendar's list view already renders the time and a colored dot in
  // its own cells (`.fc-list-event-time` / `-graphic`) — eventContent here
  // only fills the title cell, so this is title + a compact Lead/Project ·
  // participants · status line, not a rebuild of the whole row.
  const isListView = !!eventInfo?.view?.type?.startsWith("list")

  if (isListView) {
    const participantNames = event?.extendedProps?.participants
      ?.map((p: { full_name?: string }) => p?.full_name)
      .filter(Boolean)

    return (
      <Flex
        vertical
        gap={2}
        className={isCancelled ? "line-through opacity-60" : ""}
        data-testid={isCancelled ? "calendar-event-cancelled" : undefined}
      >
        <Flex align="center" gap={6}>
          <Text strong className="text-sm">
            {title}
          </Text>
          {isCancelled && (
            <Text type="danger" className="text-[11px] font-medium">
              Cancelled
            </Text>
          )}
        </Flex>
        {!!(recordName || personOrAddress || participantNames?.length) && (
          <Text type="secondary" className="text-xs">
            {[recordName, personOrAddress, participantNames?.join(", ")]
              .filter(Boolean)
              .join(" · ")}
          </Text>
        )}
      </Flex>
    )
  }

  if (isMonthView) {
    // No time here, on the same priority the grid card now follows: a
    // month cell's single narrow line has even less width to give than a
    // 15-minute grid card, so identity claims all of it rather than
    // splitting it with a `shrink-0` time that would win the fight first.
    return (
      <Flex
        align="center"
        gap={4}
        className={`overflow-hidden size-full ${isCancelled ? "line-through opacity-60" : ""}`}
        data-testid={isCancelled ? "calendar-event-cancelled" : undefined}
      >
        <TypeDot color={event?.borderColor} />
        <Text
          ellipsis={{ tooltip: title }}
          className="text-xs text-inherit font-medium leading-4 min-w-0 flex-1"
        >
          {title}
        </Text>
      </Flex>
    )
  }

  // Identity is the content on a short card, and nothing may compete with
  // it for horizontal width — a client escalation over exactly this:
  // `CalendarTimeAndType`'s time text is `shrink-0` (it either shows in
  // full or not at all — see that component), so sharing the title's row
  // with it meant time reserved its own width FIRST and the customer-name
  // headline this file's sibling decision added got whatever was left —
  // `Lead Meeting: For Arc…` next to a half-legible `7:00a…` in the Week
  // view's narrower columns, confirmed against the live production markup.
  // Time now only ever gets a full line of its own, stacked below the
  // title, once there is real height for one (`timePlacement === "stacked"`
  // — see `pickEventCardTier`); a box too short for that just shows no time
  // at all, exactly like a 15-minute card always has. The title's row is
  // never shared with anything that would shrink it.
  //
  // `leading-none` (line-height: 1) was tried first to keep each line's box
  // as close to its font size as possible, but a single-line `Text` clips
  // at exactly that box height, and a real browser's glyph metrics need
  // slightly more than 1x font-size to show a descender in full — "Meeting"
  // rendered with its "g" cut off (confirmed live; invisible in jsdom).
  // `leading-[1.2]` gives every line just enough headroom for descenders
  // without the line boxes overlapping — `pickEventCardTier`'s thresholds
  // are what keeps the box itself tall enough for however many of these
  // lines it decides to show.
  //
  // `overflow-wrap: anywhere` is a second line of defense alongside
  // `ellipsis` for a long *unbroken* value (a name with no spaces) — plain
  // `ellipsis` truncates any single-line text correctly on its own, but a
  // value long enough to be one giant "word" has, in the past, been the
  // kind of input that finds the edge case in a shared `Text` component.
  const showStackedSlot = !!timeString

  return (
    <Flex
      ref={sizeRef}
      vertical
      gap={LINE_GAP}
      className={`overflow-hidden size-full min-h-0 ${isCancelled ? "line-through opacity-60" : ""}`}
      data-testid={isCancelled ? "calendar-event-cancelled" : undefined}
    >
      <Flex align="center" gap={4} className="min-w-0 shrink-0">
        <TypeDot color={event?.borderColor} />
        <Text
          ellipsis={{ tooltip: title }}
          className={`${TITLE_SIZE_CLASS} text-inherit font-semibold leading-[1.2] min-w-0 flex-1 [overflow-wrap:anywhere]`}
        >
          {title}
        </Text>
      </Flex>

      {showStackedSlot && (
        <CalendarTimeAndType
          className={`${TIME_SIZE_CLASS} opacity-75 leading-[1.2] shrink-0 ${secondaryLineIndent}`}
          time={timeString}
        />
      )}

      {firstContextText && showContext && (
        <Text
          ellipsis={{ tooltip: firstContextText }}
          className={`${DETAIL_SIZE_CLASS} text-inherit opacity-90 leading-[1.2] shrink-0 [overflow-wrap:anywhere] ${secondaryLineIndent}`}
        >
          {firstContextText}
        </Text>
      )}

      {secondContextText && showSecondContext && (
        <Text
          ellipsis={{ tooltip: secondContextText }}
          className={`${DETAIL_SIZE_CLASS} text-inherit opacity-70 leading-[1.2] shrink-0 [overflow-wrap:anywhere] ${secondaryLineIndent}`}
        >
          {secondContextText}
        </Text>
      )}
    </Flex>
  )
}
